import React from "react"; // No need to import { Component }
import { Card, Divider, Grid, Header, Icon, Table } from "semantic-ui-react"; // Added Icon import
import "../../node_modules/react-vis/dist/style.css";
import ChartUtils from "../charts/ChartUtils";
import "../css/home.css"; // Ensure you add the CSS classes here
import Utils from "../util/Utils";
import { isMobile } from "react-device-detect";

const renderTableYearHeaders = (dataSource, yearDataKey) => {
  return Object.keys(dataSource).map((index) => {
    const dataItem = dataSource[index];
    const year = dataItem?.[yearDataKey]?.year;
    if (!year) return null;
    const headerLabel = year.substring(0, 4);
    return <Table.HeaderCell key={index}>{headerLabel}</Table.HeaderCell>;
  });
};

const renderDataCells = (
  dataSource,
  dataKey,
  format_function,
  valuePath,
  params
) => {
  return Object.keys(dataSource).map((index) => {
    const cellData = dataSource[index]?.[dataKey]?.[valuePath];
    const cellElement = Utils.TableCell(cellData, format_function, ...params);
    return React.cloneElement(cellElement, { key: index });
  });
};

const cashflowGrowthRowsConfig = [
  {
    label: "Cashflow", // Label for the first cell in the row
    dataKey: "Operating Cashflow", // Key used to access data in cfStmtValues[index]
    valuePath: "value", // Path under dataKey to get the specific value
    format: Utils.format_currency_short, // Formatting function
    params: [10, 1], // Additional params for Utils.TableCell (adjust if needed)
    trendChartKey: "Operating Cashflow", // Key for getXyDataFromValues for the tiny chart
    trendChartDataKey: "Value", // Data key for the tiny chart
  },
  {
    label: "YoY Growth",
    dataKey: "Operating Cashflow", // Use "Operating Cashflow" data for growth calculation
    valuePath: "yoyGrowth", // Use yoyGrowth property
    format: Utils.format_percent,
    params: [0.1, 1], // Additional params for Utils.TableCell (adjust if needed)
    trendChartKey: "Operating Cashflow", // Trend chart is based on Operating Cashflow data itself
    trendChartDataKey: "Growth", // Data key for the tiny chart
  },
];

const CashflowGrowthCheck = (props) => {
  const cfStmtValues = props.stockValues.cfStmtValues.slice(isMobile ? -3 : -5);

  return (
    <>
      <Divider horizontal>
        <Header as="h4">
          <Icon size="large" name="chart line" />
          Is Cash Flow positive and growing consistently ?
        </Header>
      </Divider>
      <br />

      <Grid stackable centered>
        <Grid.Row columns={{ mobile: 1, tablet: 2, computer: 3 }}>
          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderGrowthCard(
                "Operating Cashflow", // Title for the growth card
                ChartUtils.getXyDataFromValues(
                  props.stockValues.cfStmtValues, // Use cashflow statement data
                  "Operating Cashflow" // Use Operating Cashflow data key
                ),
                Utils.format_number // Formatting for the card value
              )}
            </div>
          </Grid.Column>

          <Grid.Column width={6}>
            <div style={{ padding: "0 0.5rem" }}>
              {/* Use CSS class for margin */}
              <Card fluid raised>
                <Card.Content>
                  <Card.Header textAlign="center">
                    Past Years Values
                    <Header.Subheader style={{ marginTop: "0.25em" }}>
                      (10 to 15% growth is ideal)
                    </Header.Subheader>
                  </Card.Header>
                  <Card.Description>
                    <Table
                      size="small"
                      unstackable
                      compact="very"
                      celled
                      className="table-margin-auto" // Use CSS class for margin
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Year</Table.HeaderCell>
                          {renderTableYearHeaders(
                            cfStmtValues,
                            "Operating Cashflow"
                          )}
                          <Table.HeaderCell>Trend</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {cashflowGrowthRowsConfig.map((rowConfig) => (
                          <Table.Row key={rowConfig.label}>
                            <Table.Cell>{rowConfig.label}</Table.Cell>
                            {renderDataCells(
                              cfStmtValues, // Pass the full data object
                              rowConfig.dataKey, // Pass the data key (e.g., "Operating Cashflow")
                              rowConfig.format, // Pass the format function
                              rowConfig.valuePath, // Pass the path to the specific value (.value or .yoyGrowth)
                              rowConfig.params // Pass additional params for Utils.TableCell
                            )}
                            {/* Render the Tiny Trend Chart cell for this row */}
                            {/* This chart is specific to the row's data key and trendChartKey config */}
                            {ChartUtils.renderTinyChart(
                              ChartUtils.getXyDataFromValues(
                                cfStmtValues,
                                rowConfig.trendChartKey // Use the trend chart key from config
                              ),
                              rowConfig.trendChartDataKey // Use the trend chart data key from config
                            )}
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </Card.Description>
                </Card.Content>
                <Card.Content extra>
                  {/* Unchanged descriptive text */}
                  Operating Cashflow is the amount of cash generated by the
                  company’s general operations. For an ideal company, the
                  operating cash flow would normally be higher than the net
                  income and would tend to be parallel to the net income unless
                  in case of a huge shift in company’s strategy which you should
                  investigate about.
                  <br />
                  <br />A huge deviation between income and operating cash flow
                  depicts the possibility of accounting manipulation. Operating
                  Cashflow is a very important metric when you want to analyse
                  about how reliable a company’s profit figures are.
                </Card.Content>
              </Card>
            </div>
          </Grid.Column>

          <Grid.Column width={5}>
            <div className="chart-container">
              {ChartUtils.renderObservations(
                props.title, // Assuming props.title is the stock ticker or name
                props.stockValues.operatingCashObservations // Use operatingCashObservations
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </>
  );
};

export default CashflowGrowthCheck;
